import {
  Box,
  Center,
  Image as ChakraImage,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ClientOnly, Marquee } from '@time-line/components/common';
import { Soundbars } from '@time-line/components/icons';
import { clamp } from '@time-line/components/utils';
import Image from 'next/image';
import { useMemo } from 'react';
import type { Episode } from 'types/cms/blog/Episode';
import type { Platform } from 'types/cms/blog/Platform';

import { PodcastPlayerAudio } from './PodcastPlayerAudio';
import { PodcastPlayerPlatforms } from './PodcastPlayerPlatforms';
import soundwave from './soundwave.svg';

type PodcastPlayerProps = Omit<Episode, 'transcript'> & {
  platforms: Platform[];
};

export function PodcastPlayer({
  audio,
  episodeTitle,
  podcastTitle,
  thumbnail,
  platforms,
}: PodcastPlayerProps) {
  const thumbnailSize = useMemo(() => clamp(120, 200), []);

  return (
    <Box
      borderRadius="base"
      position="relative"
      overflow="hidden"
      _after={{
        position: 'absolute',
        content: '""',
        inset: 0,
        bgColor: 'blackAlpha.400',
      }}
    >
      <ChakraImage
        src={thumbnail}
        alt=""
        position="absolute"
        inset={0}
        width="100%"
        height="100%"
        transform="scale(1.1) translate3d(0, 0, 0)"
        objectFit="cover"
        zIndex={0}
        filter="auto"
        blur="50px"
        opacity={0.75}
      />
      <Box
        pos="relative"
        zIndex={2}
        textAlign="center"
        pt={{ base: 'sm', lg: 'm' }}
        pb={{ base: 'sm', md: 'm' }}
      >
        <Center position="relative">
          <Image alt="" src={soundwave as string} fill sizes="100vw" />
          <ChakraImage
            src={thumbnail}
            alt={`${podcastTitle} podcast artwork`}
            boxSize={thumbnailSize}
            borderRadius="lg"
            borderWidth={1}
            borderColor="blackAlpha.250"
            boxShadow="0px 2px 28px 0px rgba(0, 0, 0, 0.14)"
            mx="auto"
            zIndex={1}
          />
        </Center>
        <Stack
          mt={{ base: '2xs', lg: 'sm' }}
          mb="4xs"
          color="whiteAlpha.700"
          direction="row"
          justifyContent="center"
          spacing="5xs"
          alignItems="center"
        >
          <Soundbars boxSize="2xs" />
          <Text size="overline">Podcast</Text>
        </Stack>

        <ClientOnly>
          <Marquee speed={{ base: 10, lg: 15 }} clipGradientWidth={24}>
            <Text size="title" as="h3" color="gray.50" noOfLines={1} px="xs">
              {episodeTitle}
            </Text>
            <Text size="title" as="h3" color="gray.50" noOfLines={1} px="xs">
              {episodeTitle}
            </Text>
          </Marquee>
        </ClientOnly>
        <Text size="title" pt="5xs" px="3xs" color="whiteAlpha.700">
          {podcastTitle}
        </Text>
        <Box px="sm">
          <PodcastPlayerAudio url={audio} />
        </Box>

        {platforms && <PodcastPlayerPlatforms platforms={platforms} />}
      </Box>
    </Box>
  );
}
